import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Shield, ShieldCheck, UserPlus, Search, Users, HardDrive,
  Loader2, CheckCircle2, XCircle, Clock, Pencil, Ban, Crown,
  FileText, Save, Eye, ExternalLink, LogOut, ArrowLeft,
  Activity, UserX, RefreshCw, Trash2, ChevronLeft, ChevronRight, AlertTriangle,
  FileCode2, Upload, Database,
} from "lucide-react";
import { toast } from "sonner";

const SECTORS = [
  "", "Solar & Renewable Energy", "Education & Training", "Gems & Jewellery",
  "Professional Services", "General Trading", "Construction",
  "Marketing & Advertising", "Restaurant & Food Services"
];

function DriveBadge({ status }) {
  if (status === "connected") return <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]" data-testid="drive-badge-connected"><HardDrive className="h-3 w-3 mr-1" /> Drive Active</Badge>;
  if (status === "disconnected") return <Badge className="bg-[#ef4444]/10 text-[#ef4444] text-[10px] animate-pulse" data-testid="drive-badge-disconnected"><HardDrive className="h-3 w-3 mr-1" /> Token Expired</Badge>;
  return <Badge className="bg-[#94a3b8]/10 text-[#94a3b8] text-[10px]" data-testid="drive-badge-pending"><HardDrive className="h-3 w-3 mr-1" /> Pending Setup</Badge>;
}

function StatusBadge({ status }) {
  if (status === "active") return <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>;
  if (status === "suspended") return <Badge className="bg-[#ef4444]/10 text-[#ef4444] text-[10px]"><Ban className="h-3 w-3 mr-1" /> Suspended</Badge>;
  return <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
}

function RoleBadge({ isSuperAdmin, isAdmin }) {
  if (isSuperAdmin) return <Badge className="bg-[#7c3aed]/10 text-[#7c3aed] text-[10px]"><Crown className="h-3 w-3 mr-1" /> Super Admin</Badge>;
  if (isAdmin) return <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[10px]"><Shield className="h-3 w-3 mr-1" /> Admin</Badge>;
  return null;
}

function formatRelative(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toISOString().split("T")[0];
}

const NAV_ITEMS = [
  { id: "users", label: "Users & Access", icon: Users },
  { id: "signin", label: "Sign-in Attempts", icon: Activity },
  { id: "rejected", label: "Rejected Emails", icon: UserX },
  { id: "legal", label: "Legal Documents", icon: FileText },
  { id: "tax-schemas", label: "Tax Schemas", icon: FileCode2 },
];

export default function AdminPortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("users");

  const isSuperAdmin = user?.is_super_admin === true;

  useEffect(() => {
    if (!user?.is_admin && !user?.is_super_admin) {
      navigate("/access-restricted");
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out");
      navigate("/");
    } catch (err) {
      toast.error("Logout failed");
    }
  };

  if (!user) {
    return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4338ca]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex" data-testid="admin-portal-page">
      {/* === SIDEBAR === */}
      <aside className="w-60 bg-white border-r border-[#e2e8f0] flex flex-col sticky top-0 h-screen" data-testid="admin-sidebar">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-2.5">
            <img src="/bizflowa-logo.png" alt="BizFlowa" className="h-8 w-8 rounded-lg object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="font-outfit text-base font-bold text-[#0f172a]">BizFlowa</span>
              <span className="font-outfit text-[11px] font-semibold text-[#7c3aed] tracking-wider uppercase">Admin</span>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#7c3aed]/10 text-[#7c3aed]"
                    : "text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User identity + logout */}
        <div className="border-t border-[#e2e8f0] p-3 space-y-2">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#7c3aed]/10 flex items-center justify-center text-[#7c3aed] text-xs font-bold">
                {(user.name || user.email || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-[#0f172a] truncate">{user.name || user.email}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <RoleBadge isSuperAdmin={isSuperAdmin} isAdmin={user?.is_admin} />
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-app-btn"
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to App
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-[#ef4444] hover:bg-[#ef4444]/5 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* === MAIN === */}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
          {activeView === "users" && <UsersView isSuperAdmin={isSuperAdmin} currentUser={user} navigate={navigate} />}
          {activeView === "signin" && <SigninAttemptsView />}
          {activeView === "rejected" && <RejectedEmailsView />}
          {activeView === "legal" && <LegalDocEditor />}
          {activeView === "tax-schemas" && <TaxSchemasView />}
        </div>
      </main>
    </div>
  );
}

// ============================================================
// USERS VIEW
// ============================================================
function UsersView({ isSuperAdmin, currentUser, navigate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [gstFilter, setGstFilter] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ business_sector: "", gst_type: "", access_expires_at: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchUsers = async () => {
    try {
      const r = await api.get("/akupara-admin/users");
      setUsers(r.data);
    } catch (err) {
      if (err.response?.status === 403) navigate("/access-restricted");
      else toast.error("Failed to load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { setPage(1); }, [search, sectorFilter, gstFilter, pageSize]);

  const addToWhitelist = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await api.post("/akupara-admin/whitelist", { email: newEmail.trim().toLowerCase() });
      toast.success(`${newEmail} added to whitelist`);
      setNewEmail("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally { setAdding(false); }
  };

  const toggleStatus = async (email, currentStatus) => {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    try {
      await api.put(`/akupara-admin/users/${encodeURIComponent(email)}/status`, { status: newStatus });
      toast.success(`${email} ${newStatus === "suspended" ? "suspended (sessions killed)" : "reactivated"}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleAdmin = async (email, currentIsAdmin) => {
    try {
      await api.put(`/akupara-admin/users/${encodeURIComponent(email)}/admin`, { is_admin: !currentIsAdmin });
      toast.success(`${email} ${!currentIsAdmin ? "promoted to Admin" : "demoted from Admin"}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const addAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setAddingAdmin(true);
    try {
      await api.post("/akupara-admin/add-admin", { email: newAdminEmail.trim().toLowerCase() });
      toast.success(`${newAdminEmail} added as Admin`);
      setNewAdminEmail("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add admin");
    } finally { setAddingAdmin(false); }
  };

  const openEdit = (u) => {
    setEditForm({ business_sector: u.business_sector || "", gst_type: u.gst_type || "", access_expires_at: u.access_expires_at || "" });
    setEditDialog(u);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/akupara-admin/users/${encodeURIComponent(editDialog.email)}/metadata`, editForm);
      toast.success("Updated");
      setEditDialog(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSavingEdit(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/akupara-admin/users/${encodeURIComponent(deleteTarget.email)}`);
      const counts = r.data?.deleted || {};
      toast.success(`Deleted ${deleteTarget.email} (users:${counts.users || 0}, whitelist:${counts.whitelist || 0}, sessions:${counts.sessions || 0})`);
      // Instant UI removal without refetch
      setUsers((prev) => prev.filter((u) => u.email !== deleteTarget.email));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally { setDeleting(false); }
  };

  const filtered = useMemo(() => {
    let items = users;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((u) => u.email.includes(q) || (u.name || "").toLowerCase().includes(q) || (u.business_name || "").toLowerCase().includes(q));
    }
    if (sectorFilter) items = items.filter((u) => u.business_sector === sectorFilter);
    if (gstFilter) items = items.filter((u) => u.gst_type === gstFilter);
    return items;
  }, [users, search, sectorFilter, gstFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === "active").length,
    pending: users.filter((u) => u.status === "pending").length,
    suspended: users.filter((u) => u.status === "suspended").length,
    driveConnected: users.filter((u) => u.drive_link_status === "connected").length,
  }), [users]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#4338ca]" /></div>;

  const adminUsers = filtered.filter((u) => u.is_admin || u.is_super_admin);
  const regularUsers = filtered.filter((u) => !u.is_admin && !u.is_super_admin);

  // Client-side pagination on the regular users list
  const pagedTotal = regularUsers.length;
  const totalPages = Math.max(1, Math.ceil(pagedTotal / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pagedUsers = regularUsers.slice(startIdx, startIdx + pageSize);
  const endIdx = startIdx + pagedUsers.length;

  return (
    <>
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a] mb-1">Users &amp; Access</h1>
        <p className="text-sm text-[#64748b]">Manage who can sign in and what role they hold.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="bg-white border-[#e2e8f0]"><CardContent className="p-3"><p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Total Users</p><p className="font-outfit text-xl font-bold text-[#0f172a] mt-0.5" data-testid="stat-total">{stats.total}</p></CardContent></Card>
        <Card className="bg-white border-[#10b981]/20"><CardContent className="p-3"><p className="text-[10px] text-[#10b981] uppercase tracking-wider font-medium">Active</p><p className="font-outfit text-xl font-bold text-[#10b981] mt-0.5" data-testid="stat-active">{stats.active}</p></CardContent></Card>
        <Card className="bg-white border-[#f59e0b]/20"><CardContent className="p-3"><p className="text-[10px] text-[#f59e0b] uppercase tracking-wider font-medium">Pending</p><p className="font-outfit text-xl font-bold text-[#f59e0b] mt-0.5" data-testid="stat-pending">{stats.pending}</p></CardContent></Card>
        <Card className="bg-white border-[#ef4444]/20"><CardContent className="p-3"><p className="text-[10px] text-[#ef4444] uppercase tracking-wider font-medium">Suspended</p><p className="font-outfit text-xl font-bold text-[#ef4444] mt-0.5" data-testid="stat-suspended">{stats.suspended}</p></CardContent></Card>
        <Card className="bg-white border-[#4338ca]/20"><CardContent className="p-3"><p className="text-[10px] text-[#4338ca] uppercase tracking-wider font-medium">Drive Linked</p><p className="font-outfit text-xl font-bold text-[#4338ca] mt-0.5" data-testid="stat-drive">{stats.driveConnected}</p></CardContent></Card>
      </div>

      {/* Whitelist Creator */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-sm font-semibold text-[#0f172a] flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-[#4338ca]" /> Whitelist New Email
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com" data-testid="whitelist-email-input"
              className="flex-1 h-10 border-[#e2e8f0] font-mono"
              onKeyDown={(e) => e.key === "Enter" && addToWhitelist()} />
            <Button onClick={addToWhitelist} disabled={adding || !newEmail.trim()} data-testid="add-whitelist-btn"
              className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl h-10 px-6">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Add to Whitelist
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email, name, business..."
            data-testid="admin-search" className="pl-9 h-10 border-[#e2e8f0]" />
        </div>
        <Select value={sectorFilter || "all"} onValueChange={(v) => setSectorFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 h-10 border-[#e2e8f0]" data-testid="filter-sector"><SelectValue placeholder="All Sectors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {SECTORS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={gstFilter || "all"} onValueChange={(v) => setGstFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 h-10 border-[#e2e8f0]" data-testid="filter-gst"><SelectValue placeholder="All GST Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All GST Types</SelectItem>
            <SelectItem value="registered_regular">Regular</SelectItem>
            <SelectItem value="registered_composition">Composition</SelectItem>
            <SelectItem value="unregistered">Unregistered / Exempt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Admin Team Section — only visible to Super Admins */}
      {isSuperAdmin && (
        <Card className="bg-white border-[#7c3aed]/20 shadow-sm" data-testid="admin-team-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-outfit text-sm font-semibold text-[#7c3aed] flex items-center gap-2">
                <Crown className="h-4 w-4" /> Admin Team ({adminUsers.length})
              </CardTitle>
            </div>
            <div className="flex gap-2 mt-3">
              <Input value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="Enter email address..."
                data-testid="add-admin-email-input"
                className="flex-1 h-9 border-[#7c3aed]/20 font-mono text-sm focus-visible:ring-[#7c3aed]/30"
                onKeyDown={(e) => e.key === "Enter" && addAdmin()} />
              <Button onClick={addAdmin} disabled={addingAdmin || !newAdminEmail.trim()}
                data-testid="add-admin-btn"
                className="bg-[#7c3aed] hover:bg-[#6d28d9] rounded-lg h-9 px-4 text-sm">
                {addingAdmin ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}
                Add Admin
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {adminUsers.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#94a3b8]">No admins added yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#7c3aed]/[0.03]">
                      <th className="text-left py-2.5 px-4 text-[#7c3aed] font-medium text-xs">Email</th>
                      <th className="text-left py-2.5 px-4 text-[#7c3aed] font-medium text-xs">Name</th>
                      <th className="text-center py-2.5 px-4 text-[#7c3aed] font-medium text-xs">Role</th>
                      <th className="text-center py-2.5 px-4 text-[#7c3aed] font-medium text-xs">Status</th>
                      <th className="text-right py-2.5 px-4 text-[#7c3aed] font-medium text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((u) => (
                      <tr key={u.email} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#7c3aed]/[0.02]" data-testid={`admin-row-${u.email}`}>
                        <td className="py-2.5 px-4 font-mono text-xs text-[#0f172a] font-medium">{u.email}</td>
                        <td className="py-2.5 px-4 text-xs text-[#64748b]">{u.name || "-"}</td>
                        <td className="py-2.5 px-4 text-center"><RoleBadge isSuperAdmin={u.is_super_admin} isAdmin={u.is_admin} /></td>
                        <td className="py-2.5 px-4 text-center"><StatusBadge status={u.status} /></td>
                        <td className="py-2.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!u.is_super_admin && (
                              <>
                                <Button variant="ghost" size="sm"
                                  onClick={() => toggleStatus(u.email, u.status)}
                                  className={`h-7 px-2 text-[10px] rounded-md ${u.status === "active" ? "text-[#ef4444] hover:bg-[#ef4444]/5" : "text-[#10b981] hover:bg-[#10b981]/5"}`}
                                  data-testid={`toggle-status-${u.email}`}
                                  title={u.status === "active" ? "Suspend" : "Reactivate"}>
                                  {u.status === "active" ? <Ban className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                                </Button>
                                <Button variant="ghost" size="sm"
                                  onClick={() => toggleAdmin(u.email, u.is_admin)}
                                  className="h-7 px-2 text-[10px] rounded-md text-[#f59e0b] hover:bg-[#f59e0b]/5"
                                  data-testid={`demote-admin-${u.email}`}
                                  title="Remove Admin">
                                  <Shield className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                            {u.is_super_admin && <span className="text-[10px] text-[#94a3b8]">Protected</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* User Registry Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-sm font-semibold text-[#0f172a] flex items-center gap-2">
            <Users className="h-4 w-4 text-[#64748b]" /> Users ({regularUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {regularUsers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">{search ? "No users match." : "No users yet."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Business</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">GST Type</th>
                    <th className="text-center py-3 px-4 text-[#64748b] font-medium">Drive</th>
                    <th className="text-center py-3 px-4 text-[#64748b] font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((u) => (
                    <tr key={u.email} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`user-row-${u.email}`}>
                      <td className="py-3 px-4">
                        <div className="font-mono text-xs text-[#0f172a] font-medium">{u.email}</div>
                        {u.name && <div className="text-xs text-[#94a3b8]">{u.name}</div>}
                        {u.access_created_at && <div className="text-[10px] text-[#cbd5e1]">Added: {u.access_created_at.split("T")[0]}</div>}
                      </td>
                      <td className="py-3 px-4 text-[#64748b] text-xs">{u.business_sector || <span className="text-[#cbd5e1]">-</span>}</td>
                      <td className="py-3 px-4 text-[#64748b] text-xs">
                        {u.gst_type === "registered_regular" ? "Regular" : u.gst_type === "registered_composition" ? "Composition" : u.gst_type === "unregistered" ? "Unregistered / Exempt" : <span className="text-[#cbd5e1]">-</span>}
                      </td>
                      <td className="py-3 px-4 text-center"><DriveBadge status={u.drive_link_status} /></td>
                      <td className="py-3 px-4 text-center"><StatusBadge status={u.status} /></td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-7 w-7 p-0" title="Edit metadata" data-testid={`edit-user-${u.email}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => toggleStatus(u.email, u.status)}
                            className={`h-7 px-2 text-[10px] rounded-md ${u.status === "active" || u.status === "pending" ? "text-[#ef4444] hover:bg-[#ef4444]/5" : "text-[#10b981] hover:bg-[#10b981]/5"}`}
                            data-testid={`toggle-status-${u.email}`}
                            title={u.status === "active" || u.status === "pending" ? "Suspend" : "Reactivate"}>
                            {u.status === "active" || u.status === "pending" ? <Ban className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                          </Button>
                          {isSuperAdmin && u.email !== currentUser?.email && (
                            <Button variant="ghost" size="sm"
                              onClick={() => setDeleteTarget(u)}
                              className="h-7 w-7 p-0 text-[#ef4444] hover:bg-[#ef4444]/10"
                              data-testid={`delete-user-${u.email}`}
                              title="Permanently delete user">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination bar */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#f1f5f9] bg-[#fafbfc]" data-testid="users-pagination-bar">
                <div className="text-xs text-[#64748b]" data-testid="users-pagination-info">
                  {pagedTotal === 0 ? "0 users" : `Viewing ${startIdx + 1}-${endIdx} of ${pagedTotal}`}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#94a3b8]">Rows</span>
                    <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                      <SelectTrigger className="h-7 w-[70px] border-[#e2e8f0] text-xs" data-testid="users-page-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 25, 50, 100].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                      data-testid="users-page-prev"
                      className="h-7 px-2 rounded-md border-[#e2e8f0] text-xs"
                    >
                      <ChevronLeft className="h-3 w-3 mr-0.5" /> Previous
                    </Button>
                    <span className="text-xs text-[#64748b] px-2" data-testid="users-page-label">
                      Page {safePage} of {totalPages}
                    </span>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      data-testid="users-page-next"
                      className="h-7 px-2 rounded-md border-[#e2e8f0] text-xs"
                    >
                      Next <ChevronRight className="h-3 w-3 ml-0.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Metadata Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => !open && setEditDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Edit User Metadata</DialogTitle>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-4 mt-2">
              <p className="text-xs font-mono text-[#64748b]">{editDialog.email}</p>
              <div>
                <label className="text-sm font-medium mb-1 block">Business Sector</label>
                <Select value={editForm.business_sector || "none"} onValueChange={(v) => setEditForm({ ...editForm, business_sector: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-sector"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {SECTORS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">GST Type</label>
                <Select value={editForm.gst_type || "none"} onValueChange={(v) => setEditForm({ ...editForm, gst_type: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-gst-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    <SelectItem value="registered_regular">Regular</SelectItem>
                    <SelectItem value="registered_composition">Composition</SelectItem>
                    <SelectItem value="unregistered">Unregistered / Exempt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Access Expires</label>
                <Input type="date" value={editForm.access_expires_at} onChange={(e) => setEditForm({ ...editForm, access_expires_at: e.target.value })} data-testid="edit-expires" className="border-[#e2e8f0]" />
              </div>
              <Button onClick={saveEdit} disabled={savingEdit} className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl" data-testid="save-metadata-btn">
                {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog — Super Admin only */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2 text-[#ef4444]">
              <AlertTriangle className="h-5 w-5" /> Permanently delete user?
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-[#475569] leading-relaxed">
                Are you sure you want to permanently delete <span className="font-mono font-semibold text-[#0f172a]">{deleteTarget.email}</span>?
              </p>
              <div className="bg-[#fef2f2] border border-[#ef4444]/20 rounded-lg p-3 text-xs text-[#7f1d1d] space-y-1">
                <p className="font-semibold">This will remove:</p>
                <ul className="list-disc list-inside space-y-0.5 text-[11px]">
                  <li>User identity record</li>
                  <li>Whitelist access grant</li>
                  <li>All active sessions (forces sign-out)</li>
                  <li>Google Drive credentials (if linked)</li>
                </ul>
                <p className="mt-2 text-[11px] italic">Business documents (invoices, expenses, etc.) are retained for audit/compliance.</p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  data-testid="cancel-delete-btn"
                  className="rounded-lg border-[#e2e8f0]"
                >Cancel</Button>
                <Button
                  onClick={confirmDelete}
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                  className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete permanently
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// SIGN-IN ATTEMPTS VIEW
// ============================================================
function SigninAttemptsView() {
  const [data, setData] = useState({ items: [], stats: { total: 0, success: 0, rejected: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | success | rejected

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/akupara-admin/signin-attempts?limit=200");
      setData(r.data);
    } catch (err) {
      toast.error("Failed to load sign-in attempts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return data.items;
    return data.items.filter((i) => i.outcome === filter);
  }, [data, filter]);

  return (
    <>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] mb-1">Sign-in Attempts</h1>
          <p className="text-sm text-[#64748b]">Audit log of the last 200 sign-in attempts (success + rejected).</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="refresh-signin-btn" className="rounded-lg border-[#e2e8f0]">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-white border-[#e2e8f0]"><CardContent className="p-3"><p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Total Attempts</p><p className="font-outfit text-xl font-bold text-[#0f172a] mt-0.5" data-testid="signin-stat-total">{data.stats.total}</p></CardContent></Card>
        <Card className="bg-white border-[#10b981]/20"><CardContent className="p-3"><p className="text-[10px] text-[#10b981] uppercase tracking-wider font-medium">Successful</p><p className="font-outfit text-xl font-bold text-[#10b981] mt-0.5" data-testid="signin-stat-success">{data.stats.success}</p></CardContent></Card>
        <Card className="bg-white border-[#ef4444]/20"><CardContent className="p-3"><p className="text-[10px] text-[#ef4444] uppercase tracking-wider font-medium">Rejected</p><p className="font-outfit text-xl font-bold text-[#ef4444] mt-0.5" data-testid="signin-stat-rejected">{data.stats.rejected}</p></CardContent></Card>
      </div>

      {/* Outcome filter */}
      <div className="flex items-center gap-2">
        {["all", "success", "rejected"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`signin-filter-${f}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f ? "bg-[#0f172a] text-white" : "bg-white border border-[#e2e8f0] text-[#64748b] hover:text-[#0f172a]"
            }`}
          >
            {f === "all" ? "All" : f === "success" ? "Successful" : "Rejected"}
          </button>
        ))}
      </div>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4338ca]" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">No sign-in attempts yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">When</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Name</th>
                    <th className="text-center py-3 px-4 text-[#64748b] font-medium">Outcome</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i, idx) => (
                    <tr key={`${i.email}-${i.attempted_at}-${idx}`} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`signin-row-${idx}`}>
                      <td className="py-2.5 px-4 text-xs text-[#64748b]" title={i.attempted_at}>{formatRelative(i.attempted_at)}</td>
                      <td className="py-2.5 px-4 font-mono text-xs text-[#0f172a]">{i.email}</td>
                      <td className="py-2.5 px-4 text-xs text-[#64748b]">{i.name || "-"}</td>
                      <td className="py-2.5 px-4 text-center">
                        {i.outcome === "success" ? (
                          <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> Success</Badge>
                        ) : (
                          <Badge className="bg-[#ef4444]/10 text-[#ef4444] text-[10px]"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-xs text-[#64748b] font-mono">{i.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

// ============================================================
// REJECTED EMAILS VIEW (with quick-whitelist)
// ============================================================
function RejectedEmailsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whitelistingEmail, setWhitelistingEmail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/akupara-admin/rejected-emails");
      setItems(r.data.items || []);
    } catch (err) {
      toast.error("Failed to load rejected emails");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const whitelist = async (email) => {
    setWhitelistingEmail(email);
    try {
      await api.post("/akupara-admin/whitelist", { email: email.toLowerCase() });
      toast.success(`${email} whitelisted — they can sign in now`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to whitelist");
    } finally { setWhitelistingEmail(null); }
  };

  const pendingCount = items.filter((i) => !i.already_whitelisted).length;

  return (
    <>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] mb-1">Rejected Emails</h1>
          <p className="text-sm text-[#64748b]">Unique emails that were turned away. Whitelist any you trust with one click.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="refresh-rejected-btn" className="rounded-lg border-[#e2e8f0]">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="bg-white border-[#ef4444]/20"><CardContent className="p-3 flex items-center gap-3">
        <UserX className="h-5 w-5 text-[#ef4444]" />
        <div>
          <p className="text-[10px] text-[#ef4444] uppercase tracking-wider font-medium">Awaiting Review</p>
          <p className="font-outfit text-xl font-bold text-[#ef4444]" data-testid="rejected-stat-pending">{pendingCount}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Unique Emails</p>
          <p className="font-outfit text-xl font-bold text-[#0f172a]" data-testid="rejected-stat-unique">{items.length}</p>
        </div>
      </CardContent></Card>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4338ca]" /></div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <UserX className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">No rejected sign-in attempts yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Name</th>
                    <th className="text-center py-3 px-4 text-[#64748b] font-medium">Attempts</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Last Attempt</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Reason</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.email} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`rejected-row-${i.email}`}>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2.5">
                          {i.picture ? (
                            <img src={i.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-[#ef4444]/10 flex items-center justify-center text-[#ef4444] text-[10px] font-bold">
                              {(i.email || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-mono text-xs text-[#0f172a]">{i.email}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-xs text-[#64748b]">{i.name || "-"}</td>
                      <td className="py-2.5 px-4 text-center">
                        <Badge className="bg-[#ef4444]/10 text-[#ef4444] text-[10px]">{i.attempts}</Badge>
                      </td>
                      <td className="py-2.5 px-4 text-xs text-[#64748b]" title={i.last_attempt_at}>{formatRelative(i.last_attempt_at)}</td>
                      <td className="py-2.5 px-4 text-xs text-[#64748b] font-mono">{i.last_reason || "-"}</td>
                      <td className="py-2.5 px-4 text-right">
                        {i.already_whitelisted ? (
                          <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]" data-testid={`already-whitelisted-${i.email}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Whitelisted
                          </Badge>
                        ) : (
                          <Button
                            onClick={() => whitelist(i.email)}
                            disabled={whitelistingEmail === i.email}
                            data-testid={`whitelist-rejected-${i.email}`}
                            size="sm"
                            className="bg-[#10b981] hover:bg-[#059669] text-white h-7 px-3 text-[10px] rounded-md"
                          >
                            {whitelistingEmail === i.email ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3 mr-1" />}
                            Whitelist
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}


// ============================================================
// LEGAL DOC EDITOR (unchanged behavior)
// ============================================================
function LegalDocEditor() {
  const [activeDoc, setActiveDoc] = useState("privacy");
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState({ at: "", by: "" });
  const [preview, setPreview] = useState(false);

  const docs = [
    { slug: "privacy", label: "Privacy Policy", path: "/privacy" },
    { slug: "terms", label: "Terms of Use", path: "/terms" },
  ];

  const loadDoc = async (slug) => {
    setLoading(true);
    setPreview(false);
    try {
      const r = await api.get(`/akupara-admin/legal/${slug}`);
      if (r.data.content) {
        setContent(r.data.content);
        setOriginalContent(r.data.content);
        setLastUpdated({ at: r.data.updated_at || "", by: r.data.updated_by || "" });
      } else {
        const staticR = await fetch(`/content/${slug}.md`);
        const text = await staticR.text();
        setContent(text);
        setOriginalContent("");
        setLastUpdated({ at: "", by: "" });
      }
    } catch {
      try {
        const staticR = await fetch(`/content/${slug}.md`);
        const text = await staticR.text();
        setContent(text);
        setOriginalContent("");
      } catch { setContent(""); }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadDoc(activeDoc); }, [activeDoc]);

  const saveDoc = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/akupara-admin/legal/${activeDoc}`, { content });
      setOriginalContent(content);
      setLastUpdated({ at: r.data.updated_at, by: r.data.updated_by });
      toast.success("Document saved — live on the public page now.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const hasChanges = content !== originalContent;

  return (
    <div className="space-y-4" data-testid="legal-doc-editor">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a] mb-1">Legal Documents</h1>
        <p className="text-sm text-[#64748b]">Edit the public Privacy Policy and Terms of Use. Changes go live immediately.</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {docs.map((d) => (
          <Button key={d.slug} variant={activeDoc === d.slug ? "default" : "outline"} size="sm"
            onClick={() => setActiveDoc(d.slug)}
            data-testid={`legal-tab-${d.slug}`}
            className={`rounded-lg ${activeDoc === d.slug ? "bg-[#4338ca] text-white" : "border-[#e2e8f0]"}`}>
            <FileText className="h-3.5 w-3.5 mr-1.5" /> {d.label}
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <a href={docs.find((d) => d.slug === activeDoc)?.path} target="_blank" rel="noopener noreferrer"
            className="text-xs text-[#4338ca] hover:underline flex items-center gap-1" data-testid="view-live-link">
            <ExternalLink className="h-3 w-3" /> View Live
          </a>
          <Button variant="outline" size="sm" onClick={() => setPreview(!preview)}
            className={`rounded-lg text-xs ${preview ? "bg-[#4338ca]/10 text-[#4338ca]" : "border-[#e2e8f0]"}`}
            data-testid="toggle-preview">
            <Eye className="h-3.5 w-3.5 mr-1" /> {preview ? "Edit" : "Preview"}
          </Button>
        </div>
      </div>

      {lastUpdated.at && (
        <div className="flex items-center gap-3 text-xs text-[#94a3b8]">
          <span>Last updated: {lastUpdated.at.split("T")[0]}</span>
          {lastUpdated.by && <span>by {lastUpdated.by}</span>}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#4338ca]" /></div>
      ) : preview ? (
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-8 max-w-[800px] mx-auto">
            <div className="prose prose-sm max-w-none" data-testid="legal-preview">
              {content.split("\n").map((line, i) => {
                if (line.startsWith("# ")) return <h1 key={i} className="font-outfit text-2xl font-bold text-[#0f172a] mt-6 mb-2">{line.replace("# ", "")}</h1>;
                if (line.startsWith("## ")) return <h2 key={i} className="font-outfit text-lg font-semibold text-[#0f172a] mt-6 mb-2 pb-1 border-b border-[#f1f5f9]">{line.replace("## ", "")}</h2>;
                if (line.startsWith("### ")) return <h3 key={i} className="font-outfit text-base font-semibold text-[#0f172a] mt-4 mb-1">{line.replace("### ", "")}</h3>;
                if (line.startsWith("- ")) return <li key={i} className="text-sm text-[#475569] ml-4 mb-1">{line.replace("- ", "")}</li>;
                if (line.startsWith("---")) return <hr key={i} className="my-6 border-[#e2e8f0]" />;
                if (line.trim() === "") return <div key={i} className="h-2" />;
                return <p key={i} className="text-sm text-[#475569] mb-2 leading-relaxed">{line}</p>;
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-4">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              data-testid="legal-content-editor"
              className="min-h-[500px] font-mono text-sm leading-relaxed border-[#e2e8f0] resize-y"
              placeholder="Enter markdown content..."
            />
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-[#94a3b8]">
          {hasChanges ? <span className="text-[#f59e0b] font-medium">Unsaved changes</span> : <span>No changes</span>}
        </div>
        <Button onClick={saveDoc} disabled={saving || !hasChanges} data-testid="save-legal-btn"
          className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl px-6">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Publish Changes
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// TAX SCHEMAS VIEW — drag-and-drop uploader for the department JSON schemas
// (GSTR-1 v2.2 today, more slugs added when we onboard new returns).
// =============================================================================
function TaxSchemasView() {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // slug currently uploading
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/tax-schemas");
      setSchemas(r.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load tax schemas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uploadFor = async (slug, file) => {
    if (!file) return;
    if (!/\.json$/i.test(file.name)) {
      toast.error("Only .json files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Schema file must be under 5MB");
      return;
    }
    setUploading(slug);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/admin/update-tax-schema?schema=${encodeURIComponent(slug)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Schema '${slug}' updated (${r.data.size_bytes} bytes)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="tax-schemas-view">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a]">Update Government Tax Schema</h1>
        <p className="text-sm text-[#64748b] mt-1">
          Overwrite the active department JSON schema (e.g. GSTR-1 v2.2). Uploaded files replace the
          server-side rulebook <b>immediately</b> — the next export you generate will validate against
          the new rules. A one-step <span className="font-mono">.bak</span> snapshot is kept for rollback.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-[#4338ca]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {schemas.map((s) => (
            <SchemaUploaderCard
              key={s.schema}
              schema={s}
              busy={uploading === s.schema}
              dragOver={dragOver === s.schema}
              onDragChange={(v) => setDragOver(v ? s.schema : false)}
              onUpload={(f) => uploadFor(s.schema, f)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SchemaUploaderCard({ schema, busy, dragOver, onDragChange, onUpload }) {
  const inputRef = useState(null)[0] || { current: null };
  const fileRef = useState({ current: null })[0];

  const handleDrop = (e) => {
    e.preventDefault();
    onDragChange(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  return (
    <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid={`schema-card-${schema.schema}`}>
      <CardHeader>
        <CardTitle className="text-base font-outfit text-[#0f172a] flex items-center gap-2">
          <Database className="h-4 w-4 text-[#4338ca]" />
          <span className="uppercase font-mono text-sm">{schema.schema}</span>
          <span className="text-xs text-[#94a3b8] font-normal">— {schema.filename}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-4 text-xs text-[#64748b]">
          <div>
            <p className="uppercase tracking-wider text-[10px]">Status</p>
            <p className="text-[#0f172a] font-medium mt-0.5">
              {schema.exists ? (
                <span className="inline-flex items-center gap-1 text-[#10b981]">
                  <CheckCircle2 className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[#ef4444]">
                  <XCircle className="h-3 w-3" /> Missing
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wider text-[10px]">Last updated</p>
            <p className="text-[#0f172a] font-medium mt-0.5">
              {schema.updated_at ? formatRelative(schema.updated_at) : "—"}
            </p>
          </div>
        </div>

        {/* Drag-and-drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); onDragChange(true); }}
          onDragLeave={() => onDragChange(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
          className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all p-6 text-center ${
            dragOver
              ? "border-[#4338ca] bg-[#4338ca]/5"
              : "border-[#e2e8f0] hover:border-[#4338ca]/60 hover:bg-[#f8fafc]"
          } ${busy ? "opacity-60 pointer-events-none" : ""}`}
          data-testid={`schema-drop-${schema.schema}`}
        >
          <input
            ref={(el) => (fileRef.current = el)}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
            data-testid={`schema-file-input-${schema.schema}`}
          />
          {busy ? (
            <Loader2 className="h-6 w-6 mx-auto mb-2 text-[#4338ca] animate-spin" />
          ) : (
            <Upload className={`h-6 w-6 mx-auto mb-2 ${dragOver ? "text-[#4338ca]" : "text-[#94a3b8]"}`} />
          )}
          <p className="text-sm font-medium text-[#0f172a]">
            {busy ? "Uploading…" : dragOver ? "Release to upload" : "Drop new schema here"}
          </p>
          <p className="text-xs text-[#94a3b8] mt-1">
            or <span className="text-[#4338ca] underline">click to browse</span> · .json files up to 5MB
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

